import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronDown, ChevronRight, Mic as Mic2, Music, Award, School, Brain, Share2, Coins, Shield, CircleHelp as HelpCircle, BookOpen, Video, Trophy, User, ExternalLink } from 'lucide-react';

interface Article {
  id: string;
  title: string;
  content: string;
}

interface Topic {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  articles: Article[];
}

export const HelpSupport: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTopic, setExpandedTopic] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);

  const topics: Topic[] = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <BookOpen size={24} />,
      description: 'Learn the basics of The Singing League',
      articles: [
        {
          id: 'create-account',
          title: 'Creating Your Account',
          content: `
            <h3>Welcome to The Singing League!</h3>
            <p>Getting started is easy:</p>
            <ol>
              <li>Click "Sign Up" on the homepage</li>
              <li>Enter your email address and create a password</li>
              <li>Verify your email address</li>
              <li>Complete your profile with your stage name and photo</li>
            </ol>
            <p>Once you're signed up, you'll have access to the full song catalog, recording studio, and all platform features.</p>
          `
        },
        {
          id: 'first-recording',
          title: 'Making Your First Recording',
          content: `
            <h3>Ready to Sing?</h3>
            <p>Follow these steps to create your first recording:</p>
            <ol>
              <li>Browse the Song Catalog and select a song you'd like to perform</li>
              <li>Click "Record" to enter the recording studio</li>
              <li>Allow microphone and camera permissions when prompted</li>
              <li>Practice with the song to get comfortable</li>
              <li>When ready, click "Start Recording"</li>
              <li>Sing along with the backing track and lyrics</li>
              <li>Review your performance and save it</li>
            </ol>
            <p>Your recording will be automatically analyzed and scored by our AI system.</p>
          `
        },
        {
          id: 'understanding-scores',
          title: 'Understanding Your Scores',
          content: `
            <h3>How Scoring Works</h3>
            <p>The Singing League uses advanced AI technology to analyze your performances across multiple dimensions:</p>
            <ul>
              <li><strong>Pitch Accuracy:</strong> How closely you match the correct notes</li>
              <li><strong>Timing:</strong> Your synchronization with the backing track</li>
              <li><strong>Consistency:</strong> Maintaining quality throughout the song</li>
              <li><strong>Technique:</strong> Breath control, vibrato, and vocal dynamics</li>
            </ul>
            <p>Scores range from 0-100, with higher scores indicating better performance. You can view detailed breakdowns of each scoring category in your performance reports.</p>
          `
        }
      ]
    },
    {
      id: 'recording-studio',
      title: 'Recording Studio',
      icon: <Mic2 size={24} />,
      description: 'Master the recording features',
      articles: [
        {
          id: 'studio-setup',
          title: 'Setting Up Your Studio',
          content: `
            <h3>Optimize Your Recording Experience</h3>
            <p>For the best recording quality:</p>
            <ul>
              <li><strong>Microphone:</strong> Use a quality external microphone if possible, or ensure your device's built-in mic is unobstructed</li>
              <li><strong>Environment:</strong> Record in a quiet space with minimal echo</li>
              <li><strong>Internet:</strong> Ensure a stable connection for smooth playback</li>
              <li><strong>Browser:</strong> Use Chrome or Firefox for best compatibility</li>
              <li><strong>Permissions:</strong> Grant microphone and camera access when prompted</li>
            </ul>
            <p>The studio features a draggable video preview so you can position yourself perfectly in frame.</p>
          `
        },
        {
          id: 'practice-mode',
          title: 'Using Practice Mode',
          content: `
            <h3>Perfect Your Performance</h3>
            <p>Practice mode lets you rehearse without recording:</p>
            <ul>
              <li>Play the song as many times as you need</li>
              <li>Follow along with synchronized lyrics</li>
              <li>Hear the backing track and original vocals</li>
              <li>Adjust volume levels to your preference</li>
            </ul>
            <p>When you're ready to record for real, simply click "Start Recording" to capture your performance.</p>
          `
        },
        {
          id: 'visual-effects',
          title: 'Background Effects',
          content: `
            <h3>Customize Your Video</h3>
            <p>The Singing League offers visual customization options:</p>
            <ul>
              <li><strong>Background Blur:</strong> Apply a professional blur effect to your video background</li>
              <li><strong>Position Control:</strong> Drag your video preview to reposition yourself</li>
              <li><strong>Size Adjustment:</strong> Resize your video window for the perfect composition</li>
            </ul>
            <p>These effects are processed in real-time to give your recordings a polished, professional look.</p>
          `
        }
      ]
    },
    {
      id: 'tournaments',
      title: 'Tournaments',
      icon: <Trophy size={24} />,
      description: 'Compete and win prizes',
      articles: [
        {
          id: 'joining-tournaments',
          title: 'How to Join Tournaments',
          content: `
            <h3>Compete Against Other Singers</h3>
            <p>Tournaments are competitive singing events:</p>
            <ol>
              <li>Browse available tournaments in the Tournaments section</li>
              <li>Check the tournament details, songs, and prize information</li>
              <li>Click "Register" to enter (may require coins or entry fee)</li>
              <li>Submit your best recording of the tournament song</li>
              <li>Wait for voting or judging to complete</li>
            </ol>
            <p>Tournament types include:</p>
            <ul>
              <li><strong>Public Tournaments:</strong> Open to all users</li>
              <li><strong>Category-Specific:</strong> Limited to certain skill levels or genres</li>
              <li><strong>Invitation-Only:</strong> Exclusive events for selected participants</li>
            </ul>
          `
        },
        {
          id: 'tournament-brackets',
          title: 'Understanding Brackets',
          content: `
            <h3>Tournament Progression</h3>
            <p>Most tournaments use an elimination bracket system:</p>
            <ul>
              <li>Round 1: All participants compete with their recordings</li>
              <li>Top performers advance to the next round</li>
              <li>Each round narrows the field until a champion is crowned</li>
            </ul>
            <p>You can view tournament brackets to see matchups and track your progress. Brackets can be shared with others to showcase tournament results.</p>
          `
        },
        {
          id: 'winning-prizes',
          title: 'Prizes and Rewards',
          content: `
            <h3>What You Can Win</h3>
            <p>Tournaments offer various prizes:</p>
            <ul>
              <li><strong>Coins:</strong> Virtual currency for platform features</li>
              <li><strong>Achievements:</strong> Special badges and recognition</li>
              <li><strong>Leaderboard Points:</strong> Climb the global rankings</li>
              <li><strong>Cash Prizes:</strong> Real money rewards in premium tournaments</li>
            </ul>
            <p>Prize distribution is automatic after tournament completion. Check your profile to see your winnings.</p>
          `
        }
      ]
    },
    {
      id: 'ai-features',
      title: 'AI Features',
      icon: <Brain size={24} />,
      description: 'Leverage AI coaching and analysis',
      articles: [
        {
          id: 'ai-coach',
          title: 'AI Coach',
          content: `
            <h3>Personal Vocal Coaching</h3>
            <p>The AI Coach provides personalized feedback on your performances:</p>
            <ul>
              <li>Detailed analysis of your vocal technique</li>
              <li>Specific areas for improvement</li>
              <li>Practice recommendations</li>
              <li>Progress tracking over time</li>
            </ul>
            <p>Access AI Coach from your recordings to get instant feedback and coaching tips tailored to your unique voice.</p>
          `
        },
        {
          id: 'performance-reports',
          title: 'Performance Reports',
          content: `
            <h3>Detailed Analytics</h3>
            <p>Every recording generates a comprehensive performance report:</p>
            <ul>
              <li><strong>Visual Charts:</strong> See your pitch accuracy over time</li>
              <li><strong>Score Breakdown:</strong> Understand each component of your score</li>
              <li><strong>Strengths & Weaknesses:</strong> Identify what you do well and where to improve</li>
              <li><strong>Practice Guide:</strong> Custom exercises based on your needs</li>
            </ul>
            <p>Reports can be viewed, downloaded, and shared with coaches or friends.</p>
          `
        },
        {
          id: 'ai-studio',
          title: 'AI Studio Features',
          content: `
            <h3>Advanced Audio Processing</h3>
            <p>The AI Studio offers professional-grade tools:</p>
            <ul>
              <li>Vocal isolation and enhancement</li>
              <li>Pitch correction options</li>
              <li>Audio mixing and balancing</li>
              <li>Effect processing</li>
            </ul>
            <p>Use these tools to polish your recordings and create professional-quality content.</p>
          `
        }
      ]
    },
    {
      id: 'social-sharing',
      title: 'Social & Sharing',
      icon: <Share2 size={24} />,
      description: 'Share your performances',
      articles: [
        {
          id: 'sharing-recordings',
          title: 'Sharing Your Recordings',
          content: `
            <h3>Show Off Your Voice</h3>
            <p>Share your best performances with the world:</p>
            <ul>
              <li>Each recording has a unique shareable link</li>
              <li>Share on social media platforms</li>
              <li>Embed recordings on your website or blog</li>
              <li>Control privacy settings (public, private, or unlisted)</li>
            </ul>
            <p>Shared recordings display your performance along with your score and can help build your audience.</p>
          `
        },
        {
          id: 'leaderboards',
          title: 'Leaderboards',
          content: `
            <h3>Climb the Rankings</h3>
            <p>The Singing League features multiple leaderboards:</p>
            <ul>
              <li><strong>Global Leaderboard:</strong> Top performers across all songs</li>
              <li><strong>Song-Specific:</strong> Best recordings for each song</li>
              <li><strong>Tournament Rankings:</strong> Current standings in active competitions</li>
              <li><strong>Genre Leaders:</strong> Top singers in each music category</li>
            </ul>
            <p>Leaderboards update in real-time as new recordings are submitted and scored.</p>
          `
        }
      ]
    },
    {
      id: 'academy',
      title: 'The Academy',
      icon: <School size={24} />,
      description: 'Learn and improve your skills',
      articles: [
        {
          id: 'vocal-lessons',
          title: 'Vocal Training Lessons',
          content: `
            <h3>Structured Learning Path</h3>
            <p>The Academy offers comprehensive vocal training:</p>
            <ul>
              <li>Beginner to advanced courses</li>
              <li>Technique fundamentals</li>
              <li>Genre-specific training</li>
              <li>Breathing and support exercises</li>
              <li>Performance tips and tricks</li>
            </ul>
            <p>Complete lessons to unlock achievements and improve your skills systematically.</p>
          `
        },
        {
          id: 'practice-guides',
          title: 'Practice Guides',
          content: `
            <h3>Customized Practice Plans</h3>
            <p>Get personalized practice recommendations based on:</p>
            <ul>
              <li>Your performance history</li>
              <li>Identified areas for improvement</li>
              <li>Your vocal range and style preferences</li>
              <li>Goals you want to achieve</li>
            </ul>
            <p>Practice guides include specific exercises, warm-ups, and song recommendations to help you progress faster.</p>
          `
        }
      ]
    },
    {
      id: 'coins-currency',
      title: 'Coins & Virtual Currency',
      icon: <Coins size={24} />,
      description: 'Understanding the economy',
      articles: [
        {
          id: 'earning-coins',
          title: 'How to Earn Coins',
          content: `
            <h3>Build Your Balance</h3>
            <p>Earn coins through various activities:</p>
            <ul>
              <li>Completing your first recording</li>
              <li>Daily login bonuses</li>
              <li>Winning tournaments</li>
              <li>Achieving high scores</li>
              <li>Unlocking achievements</li>
              <li>Referring friends to the platform</li>
            </ul>
            <p>Check your profile to see your current coin balance and transaction history.</p>
          `
        },
        {
          id: 'using-coins',
          title: 'What Coins Can Buy',
          content: `
            <h3>Spend Your Coins</h3>
            <p>Use coins for:</p>
            <ul>
              <li><strong>Tournament Entry:</strong> Enter premium competitions</li>
              <li><strong>Song Unlocks:</strong> Access exclusive content</li>
              <li><strong>AI Features:</strong> Enhanced analysis and coaching</li>
              <li><strong>Profile Customization:</strong> Special badges and themes</li>
              <li><strong>Priority Features:</strong> Fast-track processing</li>
            </ul>
          `
        },
        {
          id: 'purchasing-coins',
          title: 'Purchasing Coins',
          content: `
            <h3>Buy Coin Packages</h3>
            <p>Need more coins? Purchase packages:</p>
            <ul>
              <li>Various package sizes available</li>
              <li>Secure payment processing</li>
              <li>Instant delivery to your account</li>
              <li>Bonus coins with larger packages</li>
            </ul>
            <p>Visit the Purchase Coins page to see current offers and complete your transaction securely.</p>
          `
        }
      ]
    },
    {
      id: 'achievements',
      title: 'Achievements & Progress',
      icon: <Award size={24} />,
      description: 'Track your milestones',
      articles: [
        {
          id: 'achievement-types',
          title: 'Types of Achievements',
          content: `
            <h3>Unlock Badges and Rewards</h3>
            <p>Achievements recognize your progress:</p>
            <ul>
              <li><strong>Milestone Achievements:</strong> First recording, 10 songs, 100 recordings</li>
              <li><strong>Skill Achievements:</strong> Perfect pitch, high scores, consistency</li>
              <li><strong>Competition Achievements:</strong> Tournament wins, bracket victories</li>
              <li><strong>Social Achievements:</strong> Shares, followers, community engagement</li>
              <li><strong>Special Events:</strong> Limited-time achievements for events</li>
            </ul>
            <p>Each achievement awards coins and displays on your profile.</p>
          `
        },
        {
          id: 'tracking-progress',
          title: 'Tracking Your Progress',
          content: `
            <h3>Monitor Your Journey</h3>
            <p>Your profile shows:</p>
            <ul>
              <li>Total recordings and average scores</li>
              <li>Achievement progress bars</li>
              <li>Vocal range development</li>
              <li>Genre mastery</li>
              <li>Tournament history</li>
              <li>Improvement trends over time</li>
            </ul>
            <p>Use these metrics to set goals and see how far you've come.</p>
          `
        }
      ]
    },
    {
      id: 'account-settings',
      title: 'Account & Settings',
      icon: <User size={24} />,
      description: 'Manage your profile',
      articles: [
        {
          id: 'profile-settings',
          title: 'Updating Your Profile',
          content: `
            <h3>Customize Your Presence</h3>
            <p>Personalize your profile with:</p>
            <ul>
              <li>Profile photo</li>
              <li>Stage name or display name</li>
              <li>Bio and description</li>
              <li>Social media links</li>
              <li>Musical preferences and influences</li>
            </ul>
            <p>A complete profile helps you connect with other singers and showcase your personality.</p>
          `
        },
        {
          id: 'privacy-settings',
          title: 'Privacy & Visibility',
          content: `
            <h3>Control Your Privacy</h3>
            <p>Manage who can see your content:</p>
            <ul>
              <li><strong>Profile Visibility:</strong> Public or private profile</li>
              <li><strong>Recording Privacy:</strong> Set each recording as public, private, or unlisted</li>
              <li><strong>Leaderboard Participation:</strong> Opt in or out of rankings</li>
              <li><strong>Social Features:</strong> Control who can follow you or comment</li>
            </ul>
            <p>Your privacy and comfort are important to us.</p>
          `
        },
        {
          id: 'password-security',
          title: 'Password & Security',
          content: `
            <h3>Keep Your Account Safe</h3>
            <p>Security best practices:</p>
            <ul>
              <li>Use a strong, unique password</li>
              <li>Update your password regularly</li>
              <li>Never share your login credentials</li>
              <li>Log out on shared devices</li>
              <li>Report suspicious activity immediately</li>
            </ul>
            <p>If you need to reset your password, use the "Forgot Password" link on the login page.</p>
          `
        }
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: <HelpCircle size={24} />,
      description: 'Solve common issues',
      articles: [
        {
          id: 'audio-issues',
          title: 'Audio Not Working',
          content: `
            <h3>Fixing Audio Problems</h3>
            <p>If you can't hear the backing track or your recording:</p>
            <ol>
              <li>Check your device volume is turned up</li>
              <li>Ensure your browser isn't muted</li>
              <li>Try refreshing the page</li>
              <li>Check browser permissions for audio access</li>
              <li>Test with headphones or external speakers</li>
              <li>Clear browser cache and cookies</li>
            </ol>
            <p>Still having issues? Try a different browser or device.</p>
          `
        },
        {
          id: 'microphone-issues',
          title: 'Microphone Not Detected',
          content: `
            <h3>Enable Microphone Access</h3>
            <p>If your microphone isn't working:</p>
            <ol>
              <li>Check browser permissions - allow microphone access when prompted</li>
              <li>Ensure no other apps are using the microphone</li>
              <li>In browser settings, verify the correct microphone is selected</li>
              <li>Test your microphone in your device's system settings</li>
              <li>Try disconnecting and reconnecting external microphones</li>
              <li>Restart your browser</li>
            </ol>
            <p>On Chrome: Settings > Privacy and Security > Site Settings > Microphone</p>
          `
        },
        {
          id: 'camera-issues',
          title: 'Camera Not Working',
          content: `
            <h3>Enable Camera Access</h3>
            <p>To fix camera issues:</p>
            <ol>
              <li>Grant camera permissions when prompted</li>
              <li>Check if another app is using the camera</li>
              <li>Verify camera settings in your browser</li>
              <li>Ensure camera is not physically blocked</li>
              <li>Update your browser to the latest version</li>
              <li>Try incognito/private browsing mode</li>
            </ol>
            <p>Note: Camera is optional - you can still record audio-only performances.</p>
          `
        },
        {
          id: 'slow-performance',
          title: 'Slow Loading or Lag',
          content: `
            <h3>Improve Performance</h3>
            <p>If the platform is running slowly:</p>
            <ul>
              <li>Check your internet connection speed</li>
              <li>Close unnecessary browser tabs and applications</li>
              <li>Clear browser cache and cookies</li>
              <li>Disable browser extensions temporarily</li>
              <li>Use Chrome or Firefox for best performance</li>
              <li>Ensure your device meets minimum requirements</li>
              <li>Try using a wired internet connection instead of WiFi</li>
            </ul>
          `
        },
        {
          id: 'recording-failed',
          title: 'Recording Failed to Save',
          content: `
            <h3>Recover Lost Recordings</h3>
            <p>If your recording didn't save:</p>
            <ol>
              <li>Check your internet connection - recordings require stable connectivity</li>
              <li>Ensure you have enough storage space</li>
              <li>Don't close the browser tab until processing completes</li>
              <li>Check your recordings page - it may have saved</li>
              <li>Try recording again with a stable connection</li>
            </ol>
            <p>Tip: Keep the recording page open until you see the success confirmation.</p>
          `
        }
      ]
    }
  ];

  const filteredTopics = searchQuery
    ? topics
        .map(topic => ({
          ...topic,
          articles: topic.articles.filter(article =>
            article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            article.content.toLowerCase().includes(searchQuery.toLowerCase())
          )
        }))
        .filter(topic => topic.articles.length > 0)
    : topics;

  const toggleTopic = (topicId: string) => {
    setExpandedTopic(expandedTopic === topicId ? null : topicId);
    setSelectedArticle(null);
  };

  const selectArticle = (article: Article) => {
    setSelectedArticle(article);
  };

  const goBack = () => {
    setSelectedArticle(null);
  };

  return (
    <div className="text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4">Help & Support</h1>
          <p className="text-xl text-white/70 mb-8">
            Find answers and learn how to get the most out of The Singing League
          </p>

          <div className="max-w-2xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/40" size={20} />
            <input
              type="text"
              placeholder="Search for help articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-white/[0.05] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400/40"
            />
          </div>
        </div>

        {selectedArticle ? (
          <div className="max-w-4xl mx-auto">
            <button
              onClick={goBack}
              className="mb-6 text-violet-400 hover:text-violet-300 flex items-center"
            >
              <ChevronRight size={20} className="transform rotate-180 mr-1" />
              Back to Topics
            </button>

            <div className="bg-purple-950/50 backdrop-blur-sm border border-white/[0.08] rounded-xl p-8">
              <h2 className="text-3xl font-bold mb-6">{selectedArticle.title}</h2>
              <div
                className="article-content"
                dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
              />
            </div>

            <style>{`
              .article-content {
                line-height: 1.8;
                color: rgba(255, 255, 255, 0.9);
              }
              .article-content h3 {
                font-size: 1.5rem;
                font-weight: 700;
                margin-bottom: 1rem;
                color: white;
              }
              .article-content p {
                margin-bottom: 1rem;
                color: rgba(255, 255, 255, 0.8);
              }
              .article-content ul, .article-content ol {
                margin-bottom: 1.5rem;
                padding-left: 1.5rem;
              }
              .article-content li {
                margin-bottom: 0.5rem;
                color: rgba(255, 255, 255, 0.8);
              }
              .article-content ul li {
                list-style-type: disc;
              }
              .article-content ol li {
                list-style-type: decimal;
              }
              .article-content strong {
                font-weight: 600;
                color: white;
              }
            `}</style>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {filteredTopics.map((topic) => (
              <div key={topic.id} className="bg-purple-950/50 backdrop-blur-sm border border-white/[0.08] rounded-xl overflow-hidden hover:border-white/[0.15] transition-all">
                <button
                  onClick={() => toggleTopic(topic.id)}
                  className="w-full p-6 text-left"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="bg-violet-500/10 p-3 rounded-lg text-violet-400">
                      {topic.icon}
                    </div>
                    {expandedTopic === topic.id ? (
                      <ChevronDown size={20} className="text-white/60 mt-3" />
                    ) : (
                      <ChevronRight size={20} className="text-white/60 mt-3" />
                    )}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{topic.title}</h3>
                  <p className="text-white/60 text-sm">{topic.description}</p>
                  <div className="mt-3 text-sm text-violet-400">
                    {topic.articles.length} {topic.articles.length === 1 ? 'article' : 'articles'}
                  </div>
                </button>

                {expandedTopic === topic.id && (
                  <div className="border-t border-white/[0.06] bg-black/20">
                    {topic.articles.map((article) => (
                      <button
                        key={article.id}
                        onClick={() => selectArticle(article)}
                        className="w-full px-6 py-3 text-left hover:bg-white/5 transition-colors border-b border-white/5 last:border-b-0 flex items-center justify-between"
                      >
                        <span className="text-white/80 hover:text-white">{article.title}</span>
                        <ChevronRight size={16} className="text-white/40" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {filteredTopics.length === 0 && searchQuery && (
          <div className="text-center py-12">
            <HelpCircle size={48} className="mx-auto mb-4 text-white/40" />
            <h3 className="text-xl font-semibold mb-2">No results found</h3>
            <p className="text-white/60">
              Try searching with different keywords or{' '}
              <button onClick={() => setSearchQuery('')} className="text-violet-400 hover:underline">
                browse all topics
              </button>
            </p>
          </div>
        )}

        {/* Video Tutorials Banner */}
        <div className="mt-12 mb-8">
          <div className="bg-gradient-to-r from-violet-900/60 to-purple-950/60 backdrop-blur-sm border border-white/[0.08] rounded-xl p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-center gap-5 mb-6">
              <div className="w-14 h-14 rounded-full bg-violet-600/30 flex items-center justify-center flex-shrink-0">
                <Video size={28} className="text-violet-300" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-xl font-bold mb-1">Video Tutorials</h3>
                <p className="text-white/70 text-sm">
                  Watch step-by-step video guides to learn how to record, compete, and get the most out of the app.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <a
                href="https://www.youtube.com/playlist?list=PL6IHWD7uJyz4WGUZcWYmlzeH6UT_LU-rv"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-5 py-4 bg-white/[0.06] hover:bg-white/[0.12] border border-white/[0.08] rounded-lg transition-colors group"
              >
                <div className="w-10 h-10 rounded-full bg-red-600/20 flex items-center justify-center flex-shrink-0 group-hover:bg-red-600/30 transition-colors">
                  <Video size={18} className="text-red-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm group-hover:text-red-400 transition-colors">YouTube Tutorials</p>
                  <p className="text-xs text-white/50">Step-by-step video guides</p>
                </div>
                <ExternalLink size={14} className="text-white/30 group-hover:text-red-400 transition-colors flex-shrink-0" />
              </a>
              <a
                href="https://VocalFitDeck.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-5 py-4 bg-white/[0.06] hover:bg-white/[0.12] border border-white/[0.08] rounded-lg transition-colors group"
              >
                <div className="w-10 h-10 rounded-full bg-emerald-600/20 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600/30 transition-colors">
                  <Mic2 size={18} className="text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm group-hover:text-emerald-400 transition-colors">VocalFitDeck.com</p>
                  <p className="text-xs text-white/50">Vocal training exercises</p>
                </div>
                <ExternalLink size={14} className="text-white/30 group-hover:text-emerald-400 transition-colors flex-shrink-0" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <div className="bg-purple-950/50 backdrop-blur-sm border border-white/[0.08] rounded-xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">Still Need Help?</h3>
            <p className="text-white/70 mb-6">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:info@thesingingleague.com"
                className="px-6 py-3 bg-violet-600 hover:bg-violet-500 rounded-lg font-medium transition-colors"
              >
                Contact Support
              </a>
              <Link
                to="/signup"
                className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-lg font-medium transition-colors"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};
